import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { usePermissionCheck } from '../../hooks/permissionHooks/usePermissionCheck';
import { PERMISSIONS, FALLBACK_PERMISSIONS } from '../../config/permissions';

const SubscriptionPermissionTest = () => {
  const user = useSelector((state) => state.user);
  const { 
    canPerform, 
    isAuthenticated,
    isMainVendor 
  } = usePermissionCheck();

  // Test individual permissions
  const permissionTests = [
    {
      name: 'View Subscription',
      permission: PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION,
      module: PERMISSIONS.MODULES.SUBSCRIPTION,
      result: canPerform(PERMISSIONS.MODULES.SUBSCRIPTION, PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION) || isMainVendor
    },
    {
      name: 'Purchase Subscription',
      permission: PERMISSIONS.ACTIONS.PURCHASE_SUBSCRIPTION,
      module: PERMISSIONS.MODULES.SUBSCRIPTION,
      result: canPerform(PERMISSIONS.MODULES.SUBSCRIPTION, PERMISSIONS.ACTIONS.PURCHASE_SUBSCRIPTION) || isMainVendor
    },
    {
      name: 'View Subscription History',
      permission: PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION_HISTORY,
      module: PERMISSIONS.MODULES.SUBSCRIPTION,
      result: canPerform(PERMISSIONS.MODULES.SUBSCRIPTION, PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION_HISTORY) || isMainVendor
    }
  ];

  const hasAnySubscriptionPermission = permissionTests.some(test => test.result);

  if (!isAuthenticated) {
    return (
      <div className="p-6 bg-red-50 rounded-lg border border-red-200">
        <div className="flex gap-2 items-center text-red-800">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Not Authenticated</span>
        </div>
        <p className="mt-2 text-sm text-red-600">
          User is not authenticated. Cannot test permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="flex gap-2 items-center mb-4">
        <Shield className="w-6 h-6 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Subscription Permission Test</h3>
      </div>

      {/* User Info */}
      <div className="p-4 mb-6 bg-gray-50 rounded-lg">
        <h4 className="mb-2 font-medium text-gray-900">User Information</h4>
        <div className="space-y-1 text-sm text-gray-600">
          <p><strong>Authenticated:</strong> {isAuthenticated ? 'Yes' : 'No'}</p>
          <p><strong>Main Vendor:</strong> {isMainVendor ? 'Yes' : 'No'}</p>
          <p><strong>User Role:</strong> {user.userRole || 'Not set'}</p>
          <p><strong>Has Any Subscription Permission:</strong> {hasAnySubscriptionPermission ? 'Yes' : 'No'}</p>
        </div>
      </div>

      {/* Permission Tests */}
      <div className="space-y-3">
        <h4 className="font-medium text-gray-900">Permission Tests</h4>
        {permissionTests.map((test, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-3 rounded-lg border ${
              test.result 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex gap-3 justify-between items-center">
              <div className="flex gap-2 items-center">
                {test.result ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <div>
                  <p className="font-medium text-gray-900">{test.name}</p>
                  <p className="text-sm text-gray-600">
                    Module: {test.module} | Action: {test.permission}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  test.result 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {test.result ? 'GRANTED' : 'DENIED'}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Raw Permissions Data */}
      <div className="p-4 mt-6 bg-gray-50 rounded-lg">
        <h4 className="mb-2 font-medium text-gray-900">Raw Permissions Data</h4>
        <div className="text-sm text-gray-600">
          <pre className="overflow-auto whitespace-pre-wrap">
            {JSON.stringify(user.userPermissions, null, 2)}
          </pre>
        </div>
      </div>

      {/* Recommendations */}
      <div className="p-4 mt-6 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex gap-2 items-center mb-2">
          <AlertTriangle className="w-5 h-5 text-blue-600" />
          <h4 className="font-medium text-blue-900">Recommendations</h4>
        </div>
        <ul className="space-y-1 text-sm text-blue-800">
          {!hasAnySubscriptionPermission && (
            <li>• User has no subscription permissions. Consider granting at least View Subscription permission.</li>
          )}
          {permissionTests[0].result && !permissionTests[1].result && (
            <li>• User can view but not purchase subscriptions. This is a valid read-only setup.</li>
          )}
          {permissionTests[1].result && !permissionTests[0].result && (
            <li>• User can purchase but not view subscriptions. Consider granting View Subscription permission.</li>
          )}
          {permissionTests[1].result && !permissionTests[2].result && (
            <li>• User can purchase but not view history. Consider if this is the intended behavior.</li>
          )}
          {isMainVendor && (
            <li>• Main vendor has full access to all subscription operations.</li>
          )}
        </ul>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Subscription Link Visibility Test
        </h3>
        
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Current User State:</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p><strong>Is Main Vendor:</strong> {isMainVendor ? 'Yes' : 'No'}</p>
              <p><strong>User Permissions:</strong> {JSON.stringify(user.userPermissions, null, 2)}</p>
              <p><strong>User Role:</strong> {user?.userRole || 'Unknown'}</p>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">Permission Checks:</h4>
            <div className="text-sm text-green-700 space-y-1">
              <p><strong>View_Subscription:</strong> {permissionTests[0].result ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Purchase_Subscription:</strong> {permissionTests[1].result ? '✅ Yes' : '❌ No'}</p>
              <p><strong>View_Subscription_History:</strong> {permissionTests[2].result ? '✅ Yes' : '❌ No'}</p>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-medium text-yellow-800 mb-2">Link Visibility Status:</h4>
            <div className="text-sm text-yellow-700 space-y-1">
              <p><strong>Manage Subscription Link:</strong> {permissionTests[0].result ? '✅ Should Show' : '❌ Should Hide'}</p>
              <p><strong>Subscription Plans Link:</strong> {permissionTests[1].result ? '✅ Should Show' : '❌ Should Hide'}</p>
              <p><strong>Payment History Link:</strong> {permissionTests[2].result ? '✅ Should Show' : '❌ Should Hide'}</p>
            </div>
          </div>

          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-medium text-purple-800 mb-2">Debug Information:</h4>
            <div className="text-sm text-purple-700 space-y-1">
              <p><strong>Module:</strong> {PERMISSIONS.MODULES.SUBSCRIPTION}</p>
              <p><strong>Actions:</strong> {Object.values(PERMISSIONS.ACTIONS).filter(action => action.includes('Subscription')).join(', ')}</p>
              <p><strong>Fallback Permissions:</strong> {JSON.stringify(FALLBACK_PERMISSIONS.Subscription)}</p>
            </div>
          </div>

          <div className="bg-red-50 p-4 rounded-lg">
            <h4 className="font-medium text-red-800 mb-2">Troubleshooting:</h4>
            <div className="text-sm text-red-700 space-y-1">
              <p>• Check if user has subscription permissions in their role</p>
              <p>• Verify that main vendor fallback is working</p>
              <p>• Check browser console for subscription link debug logs</p>
              <p>• Ensure Links.js has correct permission configuration</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPermissionTest; 